import React, { useState } from 'react'
import { X, User, Send } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { AvailableMentor } from '@/types/matching'

interface RequestMentorModalProps {
  mentor: AvailableMentor
  isOpen: boolean
  onClose: () => void
  onSubmit: (message: string) => Promise<void>
  isSubmitting?: boolean
}

const RequestMentorModal: React.FC<RequestMentorModalProps> = ({
  mentor,
  isOpen,
  onClose,
  onSubmit,
  isSubmitting = false
}) => {
  const [message, setMessage] = useState('')
  const [error, setError] = useState('')

  if (!isOpen) return null

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (message.length < 10) {
      setError('Message must be at least 10 characters')
      return
    }

    if (message.length > 500) {
      setError('Message must be less than 500 characters')
      return
    }

    await onSubmit(message)
    setMessage('')
  }

  const handleClose = () => {
    if (!isSubmitting) {
      setMessage('')
      setError('')
      onClose()
    }
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
          onClick={handleClose}
        />

        {/* Modal */}
        <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6">
          {/* Close button */}
          <button
            onClick={handleClose}
            disabled={isSubmitting}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 disabled:opacity-50"
          >
            <X className="w-5 h-5" />
          </button>

          {/* Header */}
          <div className="mb-6">
            <h2 className="text-xl font-semibold text-gray-900">Request Mentorship</h2>
            <p className="mt-1 text-sm text-gray-600">
              Send a mentorship request to {mentor.firstName} {mentor.lastName}
            </p>
          </div>

          {/* Mentor Info */}
          <div className="flex items-start gap-4 p-4 bg-gray-50 rounded-lg mb-6">
            <div className="flex-shrink-0">
              {mentor.profilePicture ? (
                <img
                  src={mentor.profilePicture}
                  alt={`${mentor.firstName} ${mentor.lastName}`}
                  className="w-12 h-12 rounded-full object-cover"
                />
              ) : (
                <div className="w-12 h-12 rounded-full bg-primary-100 flex items-center justify-center">
                  <User className="w-6 h-6 text-primary-600" />
                </div>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-gray-900">
                {mentor.firstName} {mentor.lastName}
              </p>
              <div className="flex flex-wrap gap-1 mt-1">
                {mentor.expertise.slice(0, 3).map((skill) => (
                  <span
                    key={skill}
                    className="px-2 py-0.5 bg-primary-100 text-primary-700 rounded text-xs"
                  >
                    {skill}
                  </span>
                ))}
              </div>
              <p className="text-xs text-gray-500 mt-1">
                {mentor.currentMentees}/{mentor.maxMentees} mentees
              </p>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit}>
            <div className="mb-4">
              <label
                htmlFor="message"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Introduction Message
              </label>
              <textarea
                id="message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Introduce yourself and explain why you'd like this mentor to guide you..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-none"
                rows={4}
                disabled={isSubmitting}
                maxLength={500}
              />
              <div className="flex justify-between mt-1">
                <span className="text-xs text-red-500">{error}</span>
                <span
                  className={`text-xs ${
                    message.length > 450 ? 'text-orange-500' : 'text-gray-400'
                  }`}
                >
                  {message.length}/500
                </span>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-3 justify-end">
              <Button
                type="button"
                variant="secondary"
                onClick={handleClose}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="primary"
                disabled={isSubmitting || message.length < 10}
                isLoading={isSubmitting}
              >
                <Send className="w-4 h-4 mr-2" />
                Send Request
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

export default RequestMentorModal
